/* eslint-disable max-len */
import { useEffect } from "react";
import {
  Menu as RAMenu,
  useDataProvider,
  useSidebarState,
  useTranslate
} from "react-admin";
import * as User from "providers/user";

import StoreIcon from "@mui/icons-material/Store";
import logo from "assets/Logo.svg";
import logoSmall from "assets/LogoSmall.svg";
import NestedMenuItem from "./NestedMenuItem";
import {
  ChargeIcon,
  DashboardIcon,
  EyeOffIcon,
  EyeOnIcon,
  ScheduleIcon,
  SettingsIcon,
  StaffIcon,
  UsersIcon,
  ReportIcon
} from "assets/Icons";
import Avatar from "@mui/material/Avatar";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { useState } from "react";
import { Link } from "react-router-dom";
import Popover from "@mui/material/Popover";
import LogoutAndLanguage from "./LogoutAndLanguage";
import { Theme, useMediaQuery } from "@mui/material";

interface INestedData {
  text: string;
  activeOnUrl: string;
  restrictedRole: string[];
  activeHelper?: string;
  cyId?: string;
}

const scheduleNestedMenuData: INestedData[] = [
  // {
  //   text: "Example of Restricted for EVERYONE",
  //   activeOnUrl: "/some/route",
  //   restrictedRole: ["SUPER", "MANAGER", "OWNER", "ARTIST"]
  // },
  {
    text: "Availability",
    activeOnUrl: "/dashboard/Availability",
    restrictedRole: []
  },
  {
    text: "Appointment",
    activeOnUrl: "/dashboard/Appointment",
    restrictedRole: [],
    cyId: "appointments"
  },
  {
    text: "services_calendar",
    activeOnUrl: "/dashboard/Services-calendar",
    restrictedRole: ["LIMITED_ACCESS"]
  }
];

let salonNestedMenuDefaultData: INestedData[] = [
  {
    text: "Location",
    activeOnUrl: "/dashboard/Location",
    restrictedRole: ["ARTIST"]
  },
  {
    text: "Menus",
    activeOnUrl: "/dashboard/Menu",
    restrictedRole: ["ARTIST"]
  },
  {
    text: "Services",
    activeOnUrl: "/dashboard/Service",
    restrictedRole: ["ARTIST"]
  },
  {
    text: "AddOns",
    activeOnUrl: "/dashboard/AddOns",
    restrictedRole: ["ARTIST"]
  }
  // {
  //   text: "Equipment",
  //   activeOnUrl: "/dashboard/Facility",
  //   restrictedRole: []
  // }
];

const chargeNestedMenuData: INestedData[] = [
  {
    text: "new_charge",
    activeOnUrl: "open_new_charge_modal",
    restrictedRole: ["ARTIST"],
    activeHelper: "never_active"
  },
  {
    text: "appointment_orders",
    activeOnUrl: "/dashboard/Appointment-order",
    restrictedRole: ["ARTIST"],
    cyId: "appointment-orders"
  },
  {
    text: "walk_in_orders",
    activeOnUrl: "/dashboard/Walk-in-order",
    restrictedRole: ["ARTIST"]
  }
];
const systemNestedMenuData: INestedData[] = [
  {
    text: "Users",
    activeOnUrl: "/dashboard/User",
    restrictedRole: ["MANAGER", "OWNER", "ARTIST"]
  },
  {
    text: "Companies",
    activeOnUrl: "/dashboard/Company",
    restrictedRole: ["MANAGER", "OWNER", "ARTIST"]
  }
];
const reportingNestedMenuData: INestedData[] = [
  {
    text: "Appointment Services",
    activeOnUrl: "/dashboard/Appointment-service-reporting",
    restrictedRole: ["ARTIST", "USER", "MANAGER"]
  },
  {
    text: "Tipping",
    activeOnUrl: "/dashboard/Tipping-reporting",
    restrictedRole: ["ARTIST", "USER", "MANAGER"]
  },
  {
    text: "Transactions",
    activeOnUrl: "/dashboard/Transactions-reporting",
    restrictedRole: ["ARTIST", "USER", "MANAGER"]
  },
  {
    text: "Cancelled Appointments",
    activeOnUrl: "/dashboard/Cancelled-appointment-reporting",
    restrictedRole: ["ARTIST", "USER", "MANAGER"]
  }
];

export const Menu = () => {
  const { user, permissions } = User.useUser();

  const [open, setOpen] = useSidebarState();
  const [isProfileClicked, setIsProfileClicked] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [salonNestedMenuData, setSalonNestedMenuData] = useState<INestedData[]>(
    salonNestedMenuDefaultData
  );
  const dataProvider = useDataProvider();
  const translate = useTranslate();

  useEffect(() => {
    const fetchData = async () => {
      const { data } = await dataProvider.getList("Platform", {
        pagination: { page: 1, perPage: 1 },
        sort: { field: "id", order: "ASC" },
        filter: {}
      });
      if (
        data?.length > 0 &&
        !systemNestedMenuData?.find((item) => item.text === "platform_settings")
      ) {
        systemNestedMenuData.push({
          text: "platform_settings",
          activeOnUrl: `/dashboard/Platform/${data[0].id}/show`,
          restrictedRole: ["MANAGER", "OWNER", "ARTIST"]
        });
      }
      if (
        permissions?.companyId &&
        !salonNestedMenuData?.find((item) => item.text === "Company")
      ) {
        setSalonNestedMenuData((prev) => [
          {
            text: "Company",
            activeOnUrl: `/dashboard/Company/${permissions.companyId}/show`,
            restrictedRole: ["ARTIST"],
            activeHelper: permissions.companyId
          },
          ...prev
        ]);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSmall = useMediaQuery<Theme>("(max-width:640px)");
  const isMedium = useMediaQuery<Theme>("(max-width:1024px)");
  const isIpadSizeScreen = isMedium && !isSmall;

  const handleClosePopover = (): void => {
    setAnchorEl(null);
  };

  const isPopoverOpen = Boolean(anchorEl);
  const id = isPopoverOpen ? "profile-popover" : undefined;

  const handleProfileClick = (
    event: React.MouseEvent<HTMLDivElement>
  ): void => {
    if (!open) {
      setAnchorEl(event.currentTarget);
    } else {
      setIsProfileClicked(!isProfileClicked);
    }
  };

  const menuItemsStyles = {
    minWidth: "47px",
    backgroundColor: "#FAF9F7",
    margin: "0px 8px",
    borderRadius: "32px",
    paddingX: open ? "16px" : "12px",
    paddingY: "10px",
    gap: "12px",
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: isIpadSizeScreen ? "14px" : "16px",
    color: "rgba(48, 47, 46, 0.95)",
    marginBottom: "4px",
    "&.RaMenuItemLink-active": {
      backgroundColor: "rgba(184, 114, 42, 0.08)",
      "& svg": {
        fill: "#B8722A",
        "& g": {
          "& path": {
            fill: "#B8722A",
            fillOpacity: "1"
          }
        }
      }
    },
    "& .RaMenuItemLink-icon": {
      minWidth: isIpadSizeScreen ? "34px" : null
    }
  };

  const userRole = user.impersonate_company_id ? "OWNER" : user?.role?.role;
  const isCompanyLimited = permissions?.isCompanyLimited;
  const isCompanyBasic = permissions?.isCompanyBasic;

  function handleIsWebview() {
    if (globalThis.isReactNativeWebview === "true") {
      return true;
    }
    if (globalThis.isReactNativeWebview2 === "yes") {
      return true;
    }
    return false;
  }

  const isWebview = handleIsWebview();

  return (
    <RAMenu
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: isSmall ? "calc(100% - 66px)" : "100%",
        margin: 0,
        padding: 0,
        backgroundColor: "#FAF9F7",
        position: "relative",
        "&.RaMenu-open": {
          width: isIpadSizeScreen ? "190px" : "248px"
        },
        "&.RaMenu-open:focus-visible": {
          outline: "none"
        },
        "&.RaMenu-closed": {
          width: "64px"
        },
        "&.RaMenu-closed:focus-visible": {
          outline: "none"
        }
      }}
    >
      {!isSmall && (
        <div className="flex justify-center items-center mb-[24px] mt-[20px]">
          <Link to="/dashboard">
            {open ? (
              <div className="w-[168px]">
                <img className="w-[168px] h-[40px]" src={logo} alt="logo" />
              </div>
            ) : (
              <img className="w-[40px] h-[40px]" src={logoSmall} alt="logo" />
            )}
          </Link>
        </div>
      )}
      <div className="overflow-y-auto overflow-x-hidden h-full hide-scroll-bar relative">
        <div
          className={`${isSmall ? "mt-[16px]" : ""}`}
          guide-id="menu-nav-items"
        >
          <RAMenu.DashboardItem
            key="dashboard"
            primaryText={translate("navigation.Dashboard")}
            leftIcon={<DashboardIcon />}
            sx={menuItemsStyles}
          />
          {!isCompanyBasic &&
            userRole &&
            scheduleNestedMenuData.some(
              (item) => !item.restrictedRole.includes(userRole)
            ) && (
              <NestedMenuItem
                closeDrawer={() => setOpen(false)}
                isSmall={isSmall}
                isDrawerOpen={open}
                title="Schedule"
                cyId="schedule-menu-item"
                icon={<ScheduleIcon />}
                role={userRole}
                nestedMenuData={scheduleNestedMenuData}
                isIpadSizeScreen={isIpadSizeScreen}
              />
            )}
          {!isCompanyLimited && !isCompanyBasic && !permissions.isArtist && (
            <RAMenu.Item
              key="/dashboard/Customer"
              to="/dashboard/Customer"
              primaryText={translate("navigation.Customers")}
              data-cy="customer-menu-item"
              leftIcon={<UsersIcon />}
              sx={menuItemsStyles}
            />
          )}
          {((!isCompanyLimited && !isCompanyBasic) || userRole === "SUPER") &&
            userRole &&
            systemNestedMenuData.some(
              (item) => !item.restrictedRole.includes(userRole)
            ) && (
              <NestedMenuItem
                closeDrawer={() => setOpen(false)}
                isSmall={isSmall}
                isDrawerOpen={open}
                title="System"
                icon={
                  <StoreIcon
                    style={{
                      fillOpacity: 0.8,
                      marginLeft: "-2px"
                    }}
                  />
                }
                role={userRole}
                nestedMenuData={systemNestedMenuData}
                isIpadSizeScreen={isIpadSizeScreen}
              />
            )}
          {permissions?.isCompanyFull &&
            userRole &&
            chargeNestedMenuData.some(
              (item) => !item.restrictedRole.includes(userRole)
            ) && (
              <NestedMenuItem
                closeDrawer={() => setOpen(false)}
                isSmall={isSmall}
                isDrawerOpen={open}
                title="Charge"
                cyId="charge-menu-item"
                icon={<ChargeIcon />}
                role={userRole}
                nestedMenuData={
                  !isWebview
                    ? chargeNestedMenuData?.filter(
                        (item) => item.text !== "new_charge"
                      )
                    : chargeNestedMenuData
                }
                isIpadSizeScreen={isIpadSizeScreen}
              />
            )}
          {permissions?.isCompanyFull &&
            userRole &&
            reportingNestedMenuData.some(
              (item) => !item.restrictedRole.includes(userRole)
            ) && (
              <NestedMenuItem
                closeDrawer={() => setOpen(false)}
                isSmall={isSmall}
                isDrawerOpen={open}
                title="Reporting"
                icon={<ReportIcon />}
                role={userRole}
                nestedMenuData={reportingNestedMenuData}
                isIpadSizeScreen={isIpadSizeScreen}
              />
            )}
          {!permissions.isArtist && (
            <RAMenu.Item
              key="/dashboard/Artist"
              to="/dashboard/Artist"
              primaryText={translate("navigation.Staff")}
              leftIcon={<StaffIcon />}
              sx={menuItemsStyles}
            />
          )}
          {userRole &&
            salonNestedMenuData.some(
              (item) => !item.restrictedRole.includes(userRole)
            ) && (
              <NestedMenuItem
                closeDrawer={() => setOpen(false)}
                isSmall={isSmall}
                isDrawerOpen={open}
                title="Salon"
                icon={<SettingsIcon />}
                role={userRole}
                nestedMenuData={salonNestedMenuData}
                isIpadSizeScreen={isIpadSizeScreen}
              />
            )}
        </div>
      </div>
      {!isSmall && (
        <div className="bg-[#FAF9F7] w-full px-[16px] pt-[24px] z-10 shadowWhenShortScreen">
          {open && isProfileClicked && (
            <LogoutAndLanguage closeProfilePopover={handleClosePopover} />
          )}
          <div
            aria-hidden
            onClick={handleProfileClick}
            className={`flex items-center group cursor-pointer ${
              open ? "justify-between" : "justify-center"
            }`}
            guide-id="profile-menu-item"
          >
            <div className="flex items-center gap-2 cursor-pointer">
              <div>
                {user?.photo_url ? (
                  <img
                    className="w-[24px] h-[24px] rounded-[12px] object-cover"
                    src={user?.photo_url}
                    alt="avatar"
                  />
                ) : (
                  <Avatar
                    sx={{
                      bgcolor: "#B0ACA6",
                      width: "24px",
                      height: "24px",
                      fontFamily: "Inter",
                      fontSize: "12px",
                      fontWeight: 400
                    }}
                  >
                    {`${user?.first_name[0]}${user?.last_name[0]}`.toUpperCase()}
                  </Avatar>
                )}
              </div>
              {open && (
                <p
                  className="max-w-[155px] truncate text-[12px] font-normal font-['Inter'] capitalize whitespace-nowrap group-hover:!text-mono-black"
                  style={{ color: "rgba(48, 47, 46, 0.6)" }}
                >{`${user?.first_name} ${user?.last_name}`}</p>
              )}
            </div>
            {open && (
              <div>
                {isProfileClicked ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              </div>
            )}
          </div>
          <div className="h-px w-full my-2 bg-mono-100" />
          <div
            className={`flex items-center mb-[32px] ${
              open ? "justify-between" : "justify-center"
            }`}
          >
            <div className={`${open ? "visible" : "hidden"}`}>
              <a
                href="mailto:info@pointmentapp.com?subject=Support Request"
                className="text-[12px] font-normal font-['Inter'] whitespace-nowrap hover:underline cursor-pointer"
                style={{
                  color: "rgba(48, 47, 46, 0.95)",
                  fontSize: isIpadSizeScreen ? "11px" : "12px"
                }}
              >
                {translate("navigation.Support")}{" "}
              </a>
              <span
                className="text-[12px] font-normal font-['Inter'] whitespace-nowrap"
                style={{
                  color: "rgba(48, 47, 46, 0.4)",
                  fontSize: isIpadSizeScreen ? "11px" : "12px"
                }}
              >
                • {translate("navigation.Version")} 0.0.1
              </span>
            </div>
            <div
              className="darkIconOnHover cursor-pointer"
              aria-hidden
              onClick={() => setOpen(!open)}
            >
              {open ? <EyeOffIcon /> : <EyeOnIcon />}
            </div>
          </div>
        </div>
      )}
      <Popover
        id={id}
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        elevation={3}
        sx={{
          "& .MuiPopover-paper": {
            backgroundColor: "#fff",
            borderRadius: "8px",
            padding: "0px"
          }
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: 64
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
      >
        <LogoutAndLanguage
          isMobileView
          closeProfilePopover={handleClosePopover}
        />
      </Popover>
    </RAMenu>
  );
};
